import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import { Api } from "@/api/Api";
import { TopicModel } from "@/models/TopicModel";
import { CategoryModel } from "@/models/CategoryModel";
import { ValidityHelper } from "@/utils/ValidityHelper";
import {ReleaseNotesDto} from "@/models/dto/ReleaseNotesDto";

@Module({
  name: "contentModule",
})
export default class ContentModule extends VuexModule {
  public static readonly persisted = ["ContentModule._locale"];

  private _locale!: string;
  private _allTopics: TopicModel[] = [];
  private _allCategories: CategoryModel[] = [];
  private _activeSubCategoryId: string | number | undefined = undefined;
  private _activeTopCategoryId: string | number | undefined = undefined;
  private _activeTopic: TopicModel | undefined = undefined;
  private _expandedCategory: CategoryModel | undefined = undefined;
  private _feedbackFormOpen = false;
  private _releaseNotes: TopicModel | undefined = undefined;

  public get locale(): string {
    return this._locale;
  }

  public get allDataLoaded(): boolean {
    return this.allTopics.length > 0 && this.allCategories.length > 0;
  }

  public get allTopics(): TopicModel[] {
    return this._allTopics;
  }

  public get activeTopic(): TopicModel | undefined {
    return this._activeTopic;
  }

  public get allCategories(): CategoryModel[] {
    return this._allCategories;
  }

  public get activeCategoryId(): string | number | undefined {
    return this._activeSubCategoryId !== undefined ? this._activeSubCategoryId : this._activeTopCategoryId;
  }

  public get activeCategory(): CategoryModel | undefined {
    return this.categoryByID(this.activeCategoryId);
  }

  public get activeTopCategory(): CategoryModel | undefined {
    return this.categoryByID(this._activeTopCategoryId);
  }

  public get activeSubCategory(): CategoryModel | undefined {
    return this.categoryByID(this._activeSubCategoryId);
  }

  public get expandedCategory(): CategoryModel | undefined {
    return this._expandedCategory;
  }

  public get filteredTopics(): TopicModel[] {
    if (!this.context.rootGetters.allFiltersSet) {
      return [];
    }
    const validityHelper = new ValidityHelper();
    return validityHelper.filterTopics(
      this.allTopics,
      this.context.rootGetters.activeFilters,
      this.context.rootGetters.onlyShowNews
    );
  }

  public get filteredCategories(): CategoryModel[] {
    if (!this.context.rootGetters.allFiltersSet) {
      return [];
    }

    const validityHelper = new ValidityHelper();
    const activeCategoryId = this.activeCategoryId;
    let categories = JSON.parse(JSON.stringify(this.allCategories)) as CategoryModel[];

    categories.forEach((category) => {
      category.active = activeCategoryId === category.id;

      let subCategoryActiveFound = false;
      if (category.nodes) {
        category.nodes.forEach((subCategory) => {
          subCategory.active = activeCategoryId === subCategory.id;
          subCategoryActiveFound = subCategoryActiveFound || subCategory.active;
        });
      }
      category.activeBranch = subCategoryActiveFound;
    });

    // Validity matching
    categories = validityHelper.filterCategories(categories, this.context.rootGetters.activeFilters);
    validityHelper.populateCategoryTopics(categories, this.filteredTopics);

    // Only include categories with any active topics for current filter
    categories = categories.filter((c) => {
      c.nodes = c.nodes ? c.nodes.filter((sc) => sc.topics.length > 0) : [];
      return c.topics.length > 0 || c.nodes.length > 0;
    });

    return categories;
  }

  public get topicByID() {
    return (id: string | number): TopicModel | undefined => {
      // TODO: clean up when old static files are removed
      // if id is string (meaning it is using the old static topic file structure)
      if (typeof id === "string") {
        return this.filteredTopics.find((t) => t.nodeId === id || t.id.toString() === id);
      }
      else {
        // Using the new content nodes
        return this.filteredTopics.find((t) => t.id === id);
      }
    };
  }

  public get categoryByID() {
    return (id: string | number | undefined): CategoryModel | undefined => {
      if (typeof id === "undefined") {
        return undefined;
      }
      
      // TODO: clean up when old static files are removed
      // If it is a string, it uses the old static topic file structure.
      // If it is a number, it uses the new content nodes.
      const isStringId = typeof id === "string";
      
      let result: CategoryModel | undefined;
      this.filteredCategories.some((c) => {
        if (isStringId) {
          // should be using nodeId, but if we had to revert to old static files again, the id is used
          if (c.nodeId === id || c.id.toString() === id?.toString()) {
            result = c;
            return true;
          }
        }
        else if (c.id === id) {
            result = c;
            return true;
        }
        
        // Check subcategories
        if (c.nodes) {
          c.nodes.some((sc) => {
            if (isStringId) {
                if (sc.nodeId === id || sc.id.toString() === id?.toString()) {
                    result = sc;
                    return true;
                }
            }
            else if (sc.id === id) {
              result = sc;
              return true;
            }
          });
        }
      });

      return result;
    };
  }

  // Find parent category of given category ID
  public get parentCategoryByID() {
    return (id: string | number): CategoryModel | undefined => {
      for (let i = 0; i < this.filteredCategories.length; i++) {
        const category = this.filteredCategories[i];
        if (category.id.toString() === id.toString()) {
          return undefined; // This is a main category and has no parent
        } else if (category.nodes) {
          for (let j = 0; j < category.nodes.length; j++) {
            if (category.nodes[j].id.toString() === id.toString()) {
              return category;
            }
          }
        }
      }

      return undefined;
    };
  }

  public get releaseNotes(): TopicModel | undefined {
    return this._releaseNotes;
  }

  public get feedbackFormOpen(): boolean {
    return this._feedbackFormOpen;
  }

  @Mutation
  setLocale(locale: string): void {
    this._locale = locale;
  }

  @Mutation
  setAllTopics(topics: TopicModel[]): void {
    this._allTopics = topics;
  }

  @Mutation
  setAllCategories(categories: CategoryModel[]): void {
    this._allCategories = categories;
  }

  @Mutation
  setFeedbackFormOpen(value: boolean): void {
    this._feedbackFormOpen = value;
  }

  @Mutation
  setActiveTopic(topic: TopicModel | undefined): void {
    this._activeTopic = topic;
  }

  @Mutation
  setActiveSubCategory(category: CategoryModel | undefined): void {
    this._activeSubCategoryId = category ? category.id : undefined;
  }

  @Mutation
  setActiveTopCategory(category: CategoryModel | undefined): void {
    this._activeTopCategoryId = category ? category.id : undefined;
  }

  @Mutation
  setExpandedCategory(category: CategoryModel | undefined): void {
    this._expandedCategory = category;
  }
  
  @Mutation
  setReleaseNotes(releaseNotes: TopicModel | undefined): void {
    this._releaseNotes = releaseNotes;
  }

  @Action({ commit: "setAllTopics", rawError: true })
  async fetchAllTopics(platform: string): Promise<TopicModel[]> {
    return  await Api.loadTopicIndex(this.locale, platform);
  }

  @Action({ commit: "setAllCategories", rawError: true })
  async fetchAllCategories(platform: string): Promise<CategoryModel[]> {
    return await Api.loadCategoryIndex(this.locale, platform);
  }

  @Action({ commit: "setReleaseNotes", rawError: true })
  async fetchReleaseNotes(): Promise<TopicModel | undefined> {
    return await Api.loadReleaseNotes(this.locale);
  }
}
